import React, { useState, useMemo } from 'react';
import styles from './StoreOrdering.module.css';
import merchantStyles from './MerchantOrders.module.css';

const ITEMS_PER_PAGE = 10;

/**
 * MyOrdersList - Paginated table of orders with filters
 */
const MyOrdersList = ({ orders, onEditOrder }) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('es-MX', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('es-MX', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getDateValue = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toISOString().split('T')[0];
  };

  // Filter and sort orders
  const filteredOrders = useMemo(() => {
    let result = [...orders];

    // Filter by status
    if (statusFilter !== 'all') {
      result = result.filter(order => order.status === statusFilter);
    }

    // Filter by date
    if (dateFilter) {
      result = result.filter(order => getDateValue(order.createdAt) === dateFilter);
    }

    // Sort by creation date (newest first)
    result.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      return dateB - dateA;
    });

    return result;
  }, [orders, statusFilter, dateFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Reset to page 1 when filters change
  const handleStatusChange = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleDateChange = (date) => {
    setDateFilter(date);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setDateFilter('');
    setCurrentPage(1);
  };

  const hasActiveFilters = statusFilter !== 'all' || dateFilter !== '';

  return (
    <div className={styles['orders-table-container']}>
      {/* Filters */}
      <div className={styles['orders-filters']}>
        <div className={styles['filter-group']}>
          <label>Estado</label>
          <select
            value={statusFilter}
            onChange={(e) => handleStatusChange(e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="pending">Pendiente</option>
            <option value="ordered">Ordenado</option>
            <option value="received">Recibido</option>
          </select>
        </div>

        <div className={styles['filter-group']}>
          <label>Fecha</label>
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => handleDateChange(e.target.value)}
          />
        </div>

        {hasActiveFilters && (
          <button className={styles['clear-filters-btn']} onClick={clearFilters}>
            Limpiar
          </button>
        )}
      </div>

      {/* Table */}
      {paginatedOrders.length === 0 ? (
        <div className={styles['empty-state']}>
          <p>{hasActiveFilters ? 'No hay pedidos con estos filtros' : 'No hay pedidos'}</p>
        </div>
      ) : (
        <table className={styles['orders-table']}>
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Articulos</th>
              <th>Total</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {paginatedOrders.map(order => (
              <tr key={order.id}>
                <td>
                  <div className={styles['cell-date']}>{formatDate(order.createdAt)}</div>
                  <div className={styles['cell-time']}>{formatTime(order.createdAt)}</div>
                </td>
                <td>
                  <div className={styles['cell-items']}>
                    {order.items.slice(0, 2).map((item, idx) => (
                      <span key={idx}>
                        {item.quantity} {item.orderUnit} {item.productName}
                        {idx < Math.min(order.items.length, 2) - 1 && ', '}
                      </span>
                    ))}
                    {order.items.length > 2 && (
                      <span className={styles['more-items']}>+{order.items.length - 2} mas</span>
                    )}
                  </div>
                </td>
                <td className={styles['cell-total']}>{formatCurrency(order.total)}</td>
                <td>
                  <span className={`${merchantStyles['status-badge']} ${merchantStyles[order.status] || ''}`}>
                    {order.status}
                  </span>
                </td>
                <td>
                  {order.status === 'pending' && (
                    <button
                      className={styles['table-edit-btn']}
                      onClick={() => onEditOrder(order)}
                    >
                      Editar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles['table-pagination']}>
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            ‹
          </button>
          <span className={styles['page-info']}>
            {currentPage} of {totalPages}
          </span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            ›
          </button>
        </div>
      )}

      {/* Results count */}
      <div className={styles['results-count']}>
        {filteredOrders.length} pedido{filteredOrders.length !== 1 ? 's' : ''}
        {hasActiveFilters && ' (filtrado)'}
      </div>
    </div>
  );
};

export default MyOrdersList;
